const EHR_SERVICE_MARKER = 'EHR SERVICE RETURNED';
const VEHR_MAGIC_PREFIX = 'VEHR Magic Error';
const VEHR_PART_SEPARATOR = ' - ';

export type VehrTitleKey =
  | 'general'
  | 'encounterLocked'
  | 'userNotFound'
  | 'multipleOpenEncounters';

export type VehrErrorClassification = {
  isTenantNotFoundError: boolean;
  isUserNotFoundError: boolean;
  isDifferentUserError: boolean;
  isLockedError: boolean;
  isMultipleOpenEncountersError: boolean;
  isEhrServiceError: boolean;
  isEhrUnityBlockedError: boolean;
  isEhrUserNotCreatedError: boolean;
  isEhrEncounterLockedError: boolean;
  isEhrInvalidTokenError: boolean;
  isEhrInvalidUserError: boolean;
  isEhrTimeoutError: boolean;
  isEhrClientNotConnectedError: boolean;
  isEhrPatientRestrictedError: boolean;
  isEhrInvalidCredentialsError: boolean;
  isEhrExpiredTokenError: boolean;
  isEhrNoLicensesMatchError: boolean;
  hasFriendlyTitle: boolean;
  isKnown: boolean;
  titleKey: VehrTitleKey;
};

export const parseVehrErrorMessage = (
  message: string | null | undefined = '',
): VehrErrorClassification => {
  const safeMessage = message ?? '';
  const upperMessage = safeMessage.toUpperCase();

  const isTenantNotFoundError = safeMessage === 'TENANT_NOT_FOUND';
  const isUserNotFoundError = safeMessage === 'User Id not found';
  const isDifferentUserError =
    safeMessage === 'User already signed in with different user';
  const isLockedError = safeMessage === 'SSO_ENCOUNTER_LOCKED';
  const isMultipleOpenEncountersError = safeMessage.includes(
    'not associated with any appointment',
  );
  // emr-integration no longer sends this code. Keep it known, with the
  // general title, so a web deploy that lands before the backend shows the
  // error screen instead of the error boundary. Remove once both are out.
  const isRetiredEncounterNotListedError =
    safeMessage === 'SSO_ENCOUNTER_NOT_LISTED';

  const isEhrServiceError = upperMessage.includes(EHR_SERVICE_MARKER);
  const matchEhr = (phrase: string): boolean =>
    isEhrServiceError && upperMessage.includes(phrase.toUpperCase());

  const isEhrUnityBlockedError = matchEhr(
    'Unity calls are blocked due to circuit break',
  );
  const isEhrUserNotCreatedError = matchEhr(
    'User not created automatically due to feature flag',
  );
  const isEhrEncounterLockedError = matchEhr(
    'The encounter passed is currently locked and cannot be used at this time',
  );
  const isEhrInvalidTokenError = matchEhr('Supplied SSO Token is not valid');
  const isEhrInvalidUserError = matchEhr('Invalid user');
  const isEhrTimeoutError = matchEhr('Timeout');
  const isEhrClientNotConnectedError = matchEhr('Client is not connected');
  const isEhrPatientRestrictedError = matchEhr(
    'This patient is restricted and cannot be accessed via the current user',
  );
  const isEhrInvalidCredentialsError = matchEhr(
    'Please enter a valid login name and password',
  );
  const isEhrExpiredTokenError = matchEhr('Expired Token');
  const isEhrNoLicensesMatchError = matchEhr(
    'There are no licenses that match the service user provided. Please check your service user name and confirm licensing',
  );

  const isKnown =
    isTenantNotFoundError ||
    isUserNotFoundError ||
    isDifferentUserError ||
    isLockedError ||
    isMultipleOpenEncountersError ||
    isRetiredEncounterNotListedError ||
    isEhrServiceError;

  let titleKey: VehrTitleKey = 'general';
  if (isLockedError) titleKey = 'encounterLocked';
  else if (isUserNotFoundError) titleKey = 'userNotFound';
  else if (isMultipleOpenEncountersError) titleKey = 'multipleOpenEncounters';

  const hasFriendlyTitle = titleKey !== 'general';

  return {
    isTenantNotFoundError,
    isUserNotFoundError,
    isDifferentUserError,
    isLockedError,
    isMultipleOpenEncountersError,
    isEhrServiceError,
    isEhrUnityBlockedError,
    isEhrUserNotCreatedError,
    isEhrEncounterLockedError,
    isEhrInvalidTokenError,
    isEhrInvalidUserError,
    isEhrTimeoutError,
    isEhrClientNotConnectedError,
    isEhrPatientRestrictedError,
    isEhrInvalidCredentialsError,
    isEhrExpiredTokenError,
    isEhrNoLicensesMatchError,
    hasFriendlyTitle,
    isKnown,
    titleKey,
  };
};

export const isKnownVehrError = (message: string): boolean =>
  parseVehrErrorMessage(message).isKnown;

export const parseVehrMagicCause = (errorData: unknown): string => {
  const causeMessage =
    (errorData as { causeMessage?: string | null } | undefined)?.causeMessage ??
    '';
  if (!causeMessage.startsWith(VEHR_MAGIC_PREFIX)) return '';
  const parts = causeMessage.split(VEHR_PART_SEPARATOR);
  return parts.at(-1)?.trim() ?? '';
};
